/**
 * "Add my accounts" in a shared session (TMMAPP-0142 U3 item 18; TMMAPI-0181 U6). A member adds
 * **their own** private data: their bank accounts (`options.scopable`'s accounts, never anyone
 * else's) and their own data categories (`memberPrivate`: holdings, net worth, people's names). The
 * set is saved whole (`PUT …/private-data {accountIds, categories}`; what isn't named is removed).
 *
 * - **Adding** confirms in place first: "{members} will be able to ask about {item} in this
 *   session." Every member then sees it as the session's banner.
 * - **Removing** confirms: "Later answers won't use it. What was already said stays in the
 *   transcript."
 * - `422 ASSISTANT_SCOPE_INVALID` (another member's data, or a category that isn't a member's own)
 *   shows its catalog copy; another member's data is never offered here.
 * - **In a private room** (TMMAPP-0148 U1 item 3, `privateRoom`): the same editor and the same PUT,
 *   but what's added is the person's alone, so there's no "others will see" confirm (a removal still
 *   confirms) and the subtitle says only they and the assistant see it.
 */
import { useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantOptions, type AssistantSession } from '@/api/assistant';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { formatList } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { categoryDescription, categoryLabel } from './assistantCopy';
import { myPrivateData, privateDataError } from './privateData';

const TITLE_ID = 'assistant-private-data-title';
const SUBTITLE_ID = 'assistant-private-data-subtitle';
const CONFIRM_TITLE_ID = 'assistant-private-data-confirm-title';
const CONFIRM_BODY_ID = 'assistant-private-data-confirm-body';

interface PrivateDataDialogProps {
  session: AssistantSession;
  options: AssistantOptions;
  /** The other members' names, for the confirm; null when they couldn't load. */
  otherMembers: string[] | null;
  /** The person's own private room: nothing added here is shown to anyone else. */
  privateRoom?: boolean | undefined;
  onClose: () => void;
  onSaved: (session: AssistantSession) => void;
}

export function PrivateDataDialog({
  session,
  options,
  otherMembers,
  privateRoom = false,
  onClose,
  onSaved,
}: PrivateDataDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const initial = useMemo(() => myPrivateData(session), [session]);
  const accounts = options.scopable.filter((item) => item.kind === 'ACCOUNT' && !item.shared);
  const ownCategories = options.categories.filter((option) => option.memberPrivate);
  const [accountIds, setAccountIds] = useState<number[]>(initial.accountIds);
  const [categories, setCategories] = useState<string[]>(initial.categories);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const added = {
    accountIds: accountIds.filter((id) => !initial.accountIds.includes(id)),
    categories: categories.filter((key) => !initial.categories.includes(key)),
  };
  const removedAny =
    initial.accountIds.some((id) => !accountIds.includes(id)) ||
    initial.categories.some((key) => !categories.includes(key));
  const addedAny = added.accountIds.length > 0 || added.categories.length > 0;
  const dirty = addedAny || removedAny;
  const guard = useDialogDirtyGuard(dirty, onClose, busy);

  const addedNames = [
    ...added.accountIds.map(
      (id) => accounts.find((item) => item.id === id)?.name ?? t('assistant.shared.anAccount'),
    ),
    ...added.categories.map((key) => categoryLabel(t, key, options.categories)),
  ];
  const members =
    otherMembers !== null && otherMembers.length > 0
      ? formatList(otherMembers, locale)
      : t('assistant.shared.otherMembers');

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const saved = await assistantApi.putPrivateData(session.id, { accountIds, categories });
      onSaved(saved);
      onClose();
    } catch (caught) {
      setConfirming(false);
      setError(privateDataError(t, caught));
      guard.focusErrors();
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!dirty) {
      onClose();
      return;
    }
    // In a private room no one else sees what's added: only a removal confirms.
    if (privateRoom && !removedAny) {
      void save();
      return;
    }
    setConfirming(true);
  }

  const toggle = <T,>(list: T[], value: T, on: boolean) =>
    on
      ? [...list.filter((entry) => entry !== value), value]
      : list.filter((entry) => entry !== value);
  const help = { fontSize: 12, lineHeight: '18px', color: tokens.text2 } as const;

  if (confirming) {
    const consequence = [
      ...(addedAny && !privateRoom
        ? [
            t('assistant.privateData.addConsequence', {
              members,
              items: formatList(addedNames, locale),
            }),
          ]
        : []),
      ...(removedAny ? [t('assistant.shared.removeConsequence')] : []),
    ].join(' ');
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: CONFIRM_TITLE_ID,
          consequenceId: CONFIRM_BODY_ID,
          onClose: busy ? undefined : () => setConfirming(false),
        }}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <ConfirmPanel
          titleId={CONFIRM_TITLE_ID}
          consequenceId={CONFIRM_BODY_ID}
          title={t(
            addedAny && !privateRoom
              ? 'assistant.privateData.addTitle'
              : 'assistant.privateData.removeTitle',
          )}
          consequence={consequence}
          confirmLabel={t(
            addedAny && !privateRoom
              ? 'assistant.privateData.addConfirm'
              : 'assistant.shared.removeConfirm',
          )}
          busy={busy}
          focusHeading
          onConfirm={() => void save()}
          onCancel={() => setConfirming(false)}
        />
      </GuardedDialog>
    );
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={
        <DialogSubtitle id={SUBTITLE_ID}>
          {t(privateRoom ? 'assistant.privateRoomData.subtitle' : 'assistant.privateData.subtitle')}
        </DialogSubtitle>
      }
      subtitleId={SUBTITLE_ID}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <DialogTitle id={TITLE_ID}>{t('assistant.privateData.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {error !== null && <Alert severity="error">{error}</Alert>}
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('assistant.privateData.accounts')}</FormLabel>
              {accounts.length === 0 ? (
                <Typography variant="body2">{t('assistant.privateData.noAccounts')}</Typography>
              ) : (
                <FormGroup>
                  {accounts.map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          checked={accountIds.includes(item.id)}
                          onChange={(event) =>
                            setAccountIds((current) =>
                              toggle(current, item.id, event.target.checked),
                            )
                          }
                        />
                      }
                      label={item.name}
                    />
                  ))}
                </FormGroup>
              )}
            </FormControl>
            {ownCategories.length > 0 && (
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('assistant.privateData.categories')}</FormLabel>
                <FormGroup>
                  {ownCategories.map((option) => {
                    const description = categoryDescription(t, option.key, options.categories);
                    return (
                      <Box key={option.key} data-private-category={option.key} sx={{ py: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={categories.includes(option.key)}
                              onChange={(event) =>
                                setCategories((current) =>
                                  toggle(current, option.key, event.target.checked),
                                )
                              }
                            />
                          }
                          label={categoryLabel(t, option.key, options.categories)}
                        />
                        {description !== null && (
                          <Typography sx={{ ...help, paddingInlineStart: 4 }}>
                            {description}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </FormGroup>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={busy}>
            {t('assistant.privateData.save')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}
